import React, { useState, useEffect, useCallback } from 'react';
import { backupsAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Archive, Loader2, Download, FileJson, FileSpreadsheet, ShieldCheck, Clock, RefreshCw } from 'lucide-react';

const formatarTamanho = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatarData = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '-');

const Backups = () => {
  const [backups, setBackups] = useState([]);
  const [ultimoOk, setUltimoOk] = useState(null);
  const [agendamento, setAgendamento] = useState('');
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [baixando, setBaixando] = useState('');

  const carregar = useCallback(async () => {
    try {
      const res = await backupsAPI.listar();
      setBackups(res.data.backups || []);
      setUltimoOk(res.data.ultimo_ok || null);
      setAgendamento(res.data.agendamento || '');
    } catch (error) {
      toast.error(formatarErroAPI(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const criarBackup = async () => {
    setCriando(true);
    try {
      const res = await backupsAPI.criar();
      toast.success(`Backup concluído: ${res.data.total_vendas} vendas salvas`);
      await carregar();
    } catch (error) {
      toast.error(formatarErroAPI(error));
    } finally {
      setCriando(false);
    }
  };

  const baixar = async (backup, formato) => {
    const chave = `${backup.id}-${formato}`;
    setBaixando(chave);
    try {
      const res = await backupsAPI.baixar(backup.id, formato);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_vendas_${backup.data}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar backup');
    } finally {
      setBaixando('');
    }
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Archive className="h-8 w-8 text-red-600" />
            Backup de Vendas
          </h1>
          <p className="text-gray-500 mt-1">Cópia completa das vendas (JSON + Excel) guardada fora do banco, no Object Storage.</p>
        </div>
        <Button onClick={criarBackup} disabled={criando} data-testid="criar-backup-btn" className="bg-gradient-to-r from-red-600 to-purple-600 text-white">
          {criando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {criando ? 'Gerando backup...' : 'Fazer backup agora'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="ultimo-backup-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className={`h-8 w-8 ${ultimoOk ? 'text-green-500' : 'text-gray-400'}`} />
              <div>
                <p className="text-xs text-gray-500 uppercase">Último backup</p>
                <p className="font-semibold" data-testid="ultimo-backup-data">{ultimoOk ? formatarData(ultimoOk.criado_em) : 'Nenhum ainda'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-xs text-gray-500 uppercase">Vendas protegidas</p>
                <p className="font-semibold" data-testid="ultimo-backup-total">{ultimoOk ? ultimoOk.total_vendas : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-xs text-gray-500 uppercase">Agendamento</p>
                <p className="font-semibold text-sm" data-testid="backup-agendamento">{agendamento || 'Diário, automático'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Histórico ({backups.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" /></div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg" data-testid="backups-vazio">
              <Archive className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhum backup ainda. O primeiro automático roda hoje; ou clique em "Fazer backup agora".</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="backups-tabela">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">Data/Hora</th>
                    <th className="py-2 pr-4">Origem</th>
                    <th className="py-2 pr-4">Vendas</th>
                    <th className="py-2 pr-4">Tamanho</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Baixar</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b) => (
                    <tr key={b.id} className="border-b last:border-0" data-testid={`backup-row-${b.id}`}>
                      <td className="py-2 pr-4 whitespace-nowrap">{formatarData(b.criado_em)}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{b.origem === 'manual' ? `Manual · ${b.criado_por}` : 'Automático'}</Badge>
                      </td>
                      <td className="py-2 pr-4">{b.total_vendas ?? '-'}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{formatarTamanho((b.json_size || 0) + (b.xlsx_size || 0))}</td>
                      <td className="py-2 pr-4">
                        {b.status === 'ok'
                          ? <Badge className="bg-green-600 text-white">OK</Badge>
                          : <Badge className="bg-red-600 text-white" title={b.erro}>Erro</Badge>}
                      </td>
                      <td className="py-2">
                        {b.status === 'ok' && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => baixar(b, 'xlsx')} disabled={baixando === `${b.id}-xlsx`} data-testid={`baixar-xlsx-${b.id}`}>
                              {baixando === `${b.id}-xlsx` ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
                              <span className="ml-1">Excel</span>
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => baixar(b, 'json')} disabled={baixando === `${b.id}-json`} data-testid={`baixar-json-${b.id}`}>
                              {baixando === `${b.id}-json` ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileJson className="h-3 w-3" />}
                              <span className="ml-1">JSON</span>
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-500 flex items-center gap-2">
        <Download className="h-3 w-3" /> O JSON contém todos os campos das vendas (histórico, observações, anexos) e serve para restauração completa; o Excel é para consulta.
      </p>
    </div>
  );
};

export default Backups;
